import { useState, useEffect, useCallback, useRef } from 'react'
import { ApiError } from '@/lib/api'

interface UseApiResult<T> {
  data: T | null
  loading: boolean
  error: string | null
  // HTTP status when the failure was an ApiError, so callers can tell a genuine
  // 404 apart from a 5xx. Null when there is no error or it was non-HTTP.
  errorStatus: number | null
  refetch: () => void
}

export function useApi<T>(
  fetcher: () => Promise<T>,
  deps: unknown[] = [],
): UseApiResult<T> {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [errorStatus, setErrorStatus] = useState<number | null>(null)
  const mountedRef = useRef(true)

  const doFetch = useCallback(async () => {
    setLoading(true)
    setError(null)
    setErrorStatus(null)
    try {
      const result = await fetcher()
      if (mountedRef.current) setData(result)
    } catch (e) {
      if (mountedRef.current) {
        setError(e instanceof Error ? e.message : 'An error occurred')
        setErrorStatus(e instanceof ApiError ? e.status : null)
      }
    } finally {
      if (mountedRef.current) setLoading(false)
    }
  }, deps) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    mountedRef.current = true
    doFetch()
    return () => { mountedRef.current = false }
  }, [doFetch])

  return { data, loading, error, errorStatus, refetch: doFetch }
}
